import {
    FinancialEntityOwnershipType,
    RetirementAccountType,
    TransactionScheduleFieldsFragment,
} from '../graphql/generated';
import { isNull, isString } from '../util/types';
import { feBaseConfigs, getFEBaseConfig } from './baseConfig';
import { FieldConfig } from './config';
import {
    AnyFinancialEntity,
    ExternalFinancialEntity,
    ExternalFinancialEntityType,
    FEGetter,
    isDebt,
    PlentyGoalAccountFinancialEntity,
} from './types';

/** @deprecated Use getCurrentValue instead */
export const getBalanceValue = (
    fe: { latestSnapshot?: { balance: number } | null } | { amount: number }
): number | null => {
    if ('latestSnapshot' in fe) return fe.latestSnapshot?.balance ?? null;
    if ('amount' in fe) return fe.amount;
    return null;
};

export const getCurrentValue = (
    fe: AnyFinancialEntity,
    options?: { useNegativeForDebts: boolean }
): number | null => {
    const value = getBalanceValue(fe);
    return options?.useNegativeForDebts
        ? negativeBalanceForDebt(fe, value)
        : value;
};

const negativeBalanceForDebt = (
    fe: AnyFinancialEntity,
    balance: number | null
): number | null => {
    if (isNull(balance)) return balance;
    if (fe.type === 'PlentyGoalAccount') return balance;
    return isDebt(fe) ? -balance : balance;
};

/**
 * Returns all transaction schedules associated with a goal account.
 */
export const transactionSchedulesFromGoalAccount = (
    fe?: PlentyGoalAccountFinancialEntity | null | undefined
): TransactionScheduleFieldsFragment[] => {
    return [
        ...(fe?.transactionSchedulesByDestinationId ?? []),
        ...(fe?.transactionSchedulesBySourceId ?? []),
    ].filter((ts) => !ts.deletedAt);
};
/**
 * Returns all active (non-$0, non-paused) transaction schedules associated
 * with a goal account.
 */
export const activeTransactionSchedulesFromGoalAccount = (
    fe?: PlentyGoalAccountFinancialEntity | null | undefined
): TransactionScheduleFieldsFragment[] => {
    return transactionSchedulesFromGoalAccount(fe).filter(
        isActiveTransactionSchedule
    );
};
export const isActiveTransactionSchedule = (
    ts: TransactionScheduleFieldsFragment
): boolean => ts.status === 'ACTIVE' && ts.amount > 0;

export const getName = (
    fe: AnyFinancialEntity,
    opts: { withPlentyPrefix?: boolean; withTypeAsName?: boolean } = {
        withPlentyPrefix: true,
        withTypeAsName: false,
    }
): string => {
    if (fe.type === 'PlentyGoalAccount') {
        const goalName =
            fe.goal &&
            getGoalName(fe.goal, { withPlentyPrefix: opts.withPlentyPrefix });
        if (opts.withTypeAsName || !goalName) {
            return fe.info.accountType
                ? {
                      BROKERAGE: 'Goal account',
                      JOINT_BROKERAGE: 'Joint goal account',
                      ROTH_IRA: 'Plenty Roth IRA',
                      TRADITIONAL_IRA: 'Plenty Traditional IRA',
                  }[fe.info.accountType]
                : 'Goal account';
        }
        return goalName;
    }
    if (fe.name) return fe.name;
    return getDefaultName(fe.type);
};

export const getDefaultName: FEGetter<string> = (feType) => {
    return feBaseConfigs[feType].title;
};

export const getGoalName = (
    goal: { name: string },
    opts: { withPlentyPrefix?: boolean } = {
        withPlentyPrefix: false,
    }
) => {
    return opts.withPlentyPrefix ? `Plenty · ${goal.name}` : goal.name;
};

export const isLinkedAndDisconnected = (fe: ExternalFinancialEntity): boolean =>
    fe.linkStatus === 'MONEY_KIT_DISCONNECTED' ||
    fe.linkStatus === 'PLAID_DISCONNECTED';

export const isLinkedAndConnected = (fe: ExternalFinancialEntity): boolean =>
    fe.linkStatus === 'MONEY_KIT_CONNECTED' ||
    fe.linkStatus === 'PLAID_CONNECTED';

export const getLinkedFields = (
    fe: ExternalFinancialEntity,
    allFields: FieldConfig[]
) => {
    // Start with linked fields on root FE (not including snapshot fields)
    const linkedFields: string[] =
        'linkedFields' in fe && fe.linkedFields
            ? fe.linkedFields.filter(isString)
            : [];

    // If FE is currently linked, include all snapshot fields
    if (isLinkedAndConnected(fe)) {
        allFields
            .map((field) => field.name)
            .filter(
                (fieldName) =>
                    fe.latestSnapshot &&
                    Object.keys(fe.latestSnapshot).includes(fieldName)
            )
            .forEach((fieldName) => {
                linkedFields.push(fieldName);
            });
    }

    return linkedFields;
};

export const getOwnershipTypeOptionValues = ({
    feType,
    feJointOwnerPartnershipId,
    feIsMine,
    activePartnershipId,
}: {
    feType: ExternalFinancialEntityType;
    feJointOwnerPartnershipId: string | null | undefined;
    feIsMine: boolean;
    activePartnershipId: string | null | undefined;
}): FinancialEntityOwnershipType[] => {
    // If FE was created by partner, return Individual only
    if (!feIsMine) return ['INDIVIDUAL'];

    // If account is Joint between partners,
    // prevent switching back to Individual
    if (feJointOwnerPartnershipId) return ['JOINT'];

    // Otherwise, include joint option if it's available and user has a partner
    return getFEBaseConfig(feType).canBeJoint && activePartnershipId
        ? ['JOINT', 'INDIVIDUAL']
        : ['INDIVIDUAL'];
};

// Duplicated type::subtype+label map for consistent use across addForm select
// and combined type map
export const retirementAccountSubtypeLabelMap: Record<
    RetirementAccountType,
    string
> = {
    IRA: 'Traditional IRA',
    KEOGH: 'Keogh Plan',
    PENSION: 'Pension',
    ROTH_401K: 'Roth 401k',
    ROTH_IRA: 'Roth IRA',
    SEP_IRA: 'SEP IRA',
    THRIFT_SAVINGS_PLAN: 'Thrift Savings Plan',
    _401A: '401a',
    _401K: 'Traditional 401k',
    _403B: '403b',
    _457B: '457b',
};
